// src/pages/user/ShowcaseCertificate.tsx
// Route: /user/learn/:childId/showcase/:entryId/certificate (no portal layout)
//
// The showcase certificate, printed or saved as PDF from the browser
// (A4 landscape). Private to the family, so it carries the full name.

import { useParams } from "react-router-dom";
import { errorText } from "../../app/api/learningApi";
import { useGetShowcaseQuery } from "../../app/api/showcaseApi";

import { Trophy } from "lucide-react";
const TRACK_NAME: Record<string, string> = {
  foundation_builders: "Foundation Builders · Ages 6–9",
  explorers: "Explorers · Ages 9–12",
  innovators: "Innovators · Ages 13–18",
};

const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "long", year: "numeric" });

export default function ShowcaseCertificate() {
  const { childId = "", entryId = "" } = useParams();
  const { data, isLoading, error } = useGetShowcaseQuery(childId, { skip: !childId });
  const entry = data?.data.entries.find((e) => e.id === entryId);
  const cert = entry?.certificate;

  if (isLoading) return <p className="p-10 text-center text-sm text-[#8189a0]">Loading…</p>;
  if (error || !entry || !cert) {
    return (
      <p className="p-10 text-center text-sm text-[#b94a3a]">
        {error ? errorText(error, "Could not load the certificate") : "The certificate opens once the panel has finished."}
      </p>
    );
  }

  return (
    <div className="min-h-screen bg-[#f1f3f8] py-8 print:bg-white print:py-0">
      <style>{`@page { size: A4 landscape; margin: 0; } @media print { .no-print { display: none !important; } }`}</style>

      <div className="no-print mx-auto mb-5 flex max-w-[1000px] items-center justify-between px-4">
        <p className="text-sm text-[#5f6780]">Print it, or choose “Save as PDF” in the print window.</p>
        <button
          type="button"
          onClick={() => window.print()}
          className="rounded-xl bg-[#1b2d5e] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#24397a]"
        >
          Print / save as PDF
        </button>
      </div>

      <div
        className="relative mx-auto flex aspect-[297/210] w-full max-w-[1000px] flex-col items-center justify-center overflow-hidden bg-white px-[8%] text-center shadow-[0_20px_60px_rgba(27,45,94,0.15)] print:h-[210mm] print:w-[297mm] print:max-w-none print:shadow-none"
        style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}
      >
        <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#ebeff7]" />
        <div className="absolute -bottom-28 -right-20 h-80 w-80 rounded-full bg-[#fff1ec]" />
        <div className="absolute inset-[18px] rounded-[18px] border-[3px] border-[#1b2d5e]" />
        <div className="absolute inset-[28px] rounded-[12px] border border-[#cdd4e2]" />

        <div className="relative">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1b2d5e] text-2xl font-bold text-white">
            F
          </div>
          <p className="mt-3 text-xs font-bold uppercase tracking-[0.35em] text-[#1b2d5e]">Future Smart Lab</p>
          <h1 className="mt-6 text-[clamp(1.6rem,4vw,2.8rem)] font-bold tracking-[-0.03em] text-[#16213e]">
            Certificate of Achievement
          </h1>
          <p className="mt-4 text-sm text-[#5f6780]">This certifies that</p>
          <p className="mt-2 font-serif text-[clamp(1.8rem,5vw,3.4rem)] italic text-[#16213e]">{cert.name}</p>
          <p className="mx-auto mt-4 max-w-[640px] text-[clamp(0.85rem,1.6vw,1.05rem)] leading-relaxed text-[#3b4560]">
            presented <b>{entry.projectTitle || entry.lessonTitle}</b> at the Future Smart Lab{" "}
            <b>{entry.showcaseName}</b>, and was scored by the showcase panel.
          </p>
          {entry.result?.award && (
            <p className="mt-4 inline-block rounded-full bg-[#fff4d6] px-5 py-1.5 text-sm font-bold text-[#a0620f]">
              <Trophy className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{entry.result.award}
            </p>
          )}
          <div className="mt-8 flex items-end justify-center gap-16 text-xs text-[#5f6780]">
            <div>
              <p className="font-semibold text-[#16213e]">{fmtDay(cert.date)}</p>
              <div className="mt-1 h-px w-40 bg-[#cdd4e2]" />
              <p className="mt-1">Date</p>
            </div>
            <div>
              <p className="font-semibold text-[#16213e]">{TRACK_NAME[cert.track] ?? cert.track}</p>
              <div className="mt-1 h-px w-48 bg-[#cdd4e2]" />
              <p className="mt-1">Programme</p>
            </div>
            <div>
              <p className="font-semibold text-[#16213e]">The Showcase Panel</p>
              <div className="mt-1 h-px w-40 bg-[#cdd4e2]" />
              <p className="mt-1">Future Smart Lab</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
